import RequestStatus from '../model/RequestStatus';
import {
  ADDITIONAL_NEW_PROGRAM_FEES_QUERY_START,
  NEW_PROGRAM_FEES_QUERY_FAILED,
  NEW_PROGRAM_FEES_QUERY_START,
  NEW_PROGRAM_FEES_QUERY_SUCCESS,
  NEW_PROGRAM_FEES_WITH_TAXES_QUERY_FAILED,
  NEW_PROGRAM_FEES_WITH_TAXES_QUERY_START,
  NEW_PROGRAM_FEES_WITH_TAXES_QUERY_SUCCESS,
  UPDATE_NEW_PROGRAM_FEES_QUERY_START,
} from 'src/actions/NewProgramFeesQueryAction';
import {
  GetProgramFeesResponse,
  ProgramFeeResult,
  ProgramFeeResultWithStatus,
  ProgramFees,
  ProgramFeesStore,
} from 'src/model/ProgramFees';
import {
  ADDITIONAL_PROGRAM_FEES_QUERY_START,
  GetProgramFeesQueryComplete,
  PROGRAM_FEES_QUERY_FAILED,
  PROGRAM_FEES_QUERY_START,
  PROGRAM_FEES_QUERY_SUCCESS,
  PROGRAM_FEES_WITH_TAXES_QUERY_FAILED,
  PROGRAM_FEES_WITH_TAXES_QUERY_START,
  PROGRAM_FEES_WITH_TAXES_QUERY_SUCCESS,
  REMOVE_CURRENT_PROGRAM_FEE,
  UPDATE_PROGRAM_FEES_QUERY_START,
  WIDGET_PROGRAM_FEES_QUERY_START,
} from 'src/actions/ProgramFeesQueryAction';
import {
  SWITCH_AMAZON_STORE_START,
  SWITCH_AMAZON_STORE_START_NEW,
} from 'src/actions/SelectedProductQueryAction';

const initialState: GetProgramFeesResponse = {
  requestStatus: RequestStatus.NOT_LOADED,
  programFeesStore: null,
};

export default (
  state: GetProgramFeesResponse = initialState,
  action: GetProgramFeesQueryComplete,
): GetProgramFeesResponse => {
  switch (action.type) {
    case NEW_PROGRAM_FEES_QUERY_START:
    case PROGRAM_FEES_QUERY_START:
    case WIDGET_PROGRAM_FEES_QUERY_START:
    case SWITCH_AMAZON_STORE_START:
      return {
        requestStatus: RequestStatus.LOADING,
        programFeesStore: null,
      };
    case UPDATE_NEW_PROGRAM_FEES_QUERY_START:
    case UPDATE_PROGRAM_FEES_QUERY_START: {
      const updatedProgramFeesStore = updateProgramsStatus(
        state.programFeesStore,
        action.programIdList,
        RequestStatus.UPDATING,
        RequestStatus.NOT_LOADED,
      );
      return {
        requestStatus: RequestStatus.SUCCESS,
        programFeesStore: updatedProgramFeesStore,
      };
    }
    case NEW_PROGRAM_FEES_WITH_TAXES_QUERY_START:
    case PROGRAM_FEES_WITH_TAXES_QUERY_START: {
      const updatedProgramFeesWithTaxesStore = updateTaxStatus(
        state.programFeesStore,
        action.programIdList,
        RequestStatus.UPDATING,
      );
      return {
        requestStatus: state.requestStatus,
        programFeesStore: updatedProgramFeesWithTaxesStore,
      };
    }
    case NEW_PROGRAM_FEES_QUERY_SUCCESS:
    case PROGRAM_FEES_QUERY_SUCCESS: {
      const programFeesStore: ProgramFeesStore = mergeProgramFeesStore(
        state.programFeesStore,
        action.programFees,
        RequestStatus.NOT_LOADED,
      );
      return {
        requestStatus: RequestStatus.SUCCESS,
        programFeesStore,
      };
    }
    case NEW_PROGRAM_FEES_WITH_TAXES_QUERY_SUCCESS:
    case PROGRAM_FEES_WITH_TAXES_QUERY_SUCCESS: {
      const programFeesWithTaxesStore: ProgramFeesStore = mergeProgramFeesStore(
        state.programFeesStore,
        action.programFees,
        RequestStatus.SUCCESS,
      );
      return {
        requestStatus: RequestStatus.SUCCESS,
        programFeesStore: programFeesWithTaxesStore,
      };
    }
    case ADDITIONAL_PROGRAM_FEES_QUERY_START:
    case ADDITIONAL_NEW_PROGRAM_FEES_QUERY_START: {
      const programFeesStoreWithLoadingPrograms = addAdditionalPrograms(
        state.programFeesStore,
        action.programIdList,
        RequestStatus.LOADING,
      );
      return {
        requestStatus: RequestStatus.SUCCESS,
        programFeesStore: programFeesStoreWithLoadingPrograms,
      };
    }
    case NEW_PROGRAM_FEES_QUERY_FAILED:
    case PROGRAM_FEES_QUERY_FAILED: {
      const programFeesStoreWithErrorPrograms = addAdditionalPrograms(
        state.programFeesStore,
        action.programIdList,
        RequestStatus.ERROR,
      );
      return {
        requestStatus:
          state.programFeesStore == null
            ? RequestStatus.ERROR
            : state.requestStatus,
        programFeesStore: programFeesStoreWithErrorPrograms,
      };
    }
    case NEW_PROGRAM_FEES_WITH_TAXES_QUERY_FAILED:
    case PROGRAM_FEES_WITH_TAXES_QUERY_FAILED: {
      const programFeesWithTaxesErrorPrograms = updateTaxStatus(
        state.programFeesStore,
        action.programIdList,
        RequestStatus.ERROR,
      );
      return {
        requestStatus: state.requestStatus,
        programFeesStore: programFeesWithTaxesErrorPrograms,
      };
    }
    case SWITCH_AMAZON_STORE_START_NEW:
      return {
        ...state,
        requestStatus: RequestStatus.NOT_LOADED,
      };
    case REMOVE_CURRENT_PROGRAM_FEE: {
      const curProgramFeeMap =
        state.programFeesStore.programFeeResultWithStatusMap;
      delete curProgramFeeMap[action.programIdList[0]];
      return {
        requestStatus: state.requestStatus,
        programFeesStore: {
          ...state.programFeesStore,
          programFeeResultWithStatusMap: curProgramFeeMap,
        },
      };
    }
    default:
      return state;
  }
};

/**
 * Update programs' status within application store
 * @param programFeesStore programFees data in application store
 * @param programIdList programs to be update
 * @param requestStatus RequestStatus to be used to set for programs
 * @param taxStatus RequestStatus to be used to set for taxes
 * @returns new ProgramFeesStore or null
 */
function updateProgramsStatus(
  programFeesStore: ProgramFeesStore,
  programIdList: string[],
  requestStatus: RequestStatus,
  taxStatus: RequestStatus,
): ProgramFeesStore {
  const programFeeResultWithStatusMap =
    programFeesStore.programFeeResultWithStatusMap;
  const res: Record<string, ProgramFeeResultWithStatus> = {};
  if (programFeeResultWithStatusMap != null) {
    for (const key in programFeeResultWithStatusMap) {
      if (programIdList.includes(key)) {
        res[key] = {
          taxStatus,
          requestStatus,
          programFeeResult: programFeeResultWithStatusMap[key].programFeeResult,
        };
      } else {
        res[key] = programFeeResultWithStatusMap[key];
      }
    }
  }
  return {
    ...programFeesStore,
    programFeeResultWithStatusMap: res,
  };
}

/**
 * Update tax' status within application store
 * @param programFeesStore programFees data in application store
 * @param programIdList programs to be update
 * @param taxStatus RequestStatus to be used to set for taxes
 * @returns new ProgramFeesStore or null
 */
function updateTaxStatus(
  programFeesStore: ProgramFeesStore,
  programIdList: string[],
  taxStatus: RequestStatus,
): ProgramFeesStore {
  const programFeeResultWithStatusMap =
    programFeesStore.programFeeResultWithStatusMap;
  const res: Record<string, ProgramFeeResultWithStatus> = {};
  if (programFeeResultWithStatusMap != null) {
    for (const key in programFeeResultWithStatusMap) {
      if (programIdList.includes(key)) {
        res[key] = {
          taxStatus,
          requestStatus: programFeeResultWithStatusMap[key].requestStatus,
          programFeeResult: programFeeResultWithStatusMap[key].programFeeResult,
        };
      } else {
        res[key] = programFeeResultWithStatusMap[key];
      }
    }
  }
  return {
    ...programFeesStore,
    programFeeResultWithStatusMap: res,
  };
}

/**
 * Add programs into application store with specific RequestStatus
 * @param programFeesStore programFees data in application store
 * @param programIdList programs to be adding
 * @param requestStatus RequestStatus to be used to set for programs
 * @returns new ProgramFeesStore or null
 */
function addAdditionalPrograms(
  programFeesStore: ProgramFeesStore,
  programIdList: string[],
  requestStatus: RequestStatus,
): ProgramFeesStore | null {
  if (programFeesStore == null) {
    return null;
  }
  const programFeeResultWithStatusMap = buildProgramFeeResultWithStatusMap(
    programIdList,
    requestStatus,
  );
  const addedAdditionalLoadingPrograms = mergeProgramFeeResultWithStatusMap(
    programFeesStore.programFeeResultWithStatusMap,
    programFeeResultWithStatusMap,
  );
  return {
    ...programFeesStore,
    programFeeResultWithStatusMap: addedAdditionalLoadingPrograms,
  };
}

function buildProgramFeeResultWithStatusMap(
  programIdList: string[],
  requestStatus: RequestStatus,
) {
  const res: Record<string, ProgramFeeResultWithStatus> = {};
  programIdList.map((programId) => {
    res[programId] = {
      taxStatus: RequestStatus.NOT_LOADED,
      requestStatus,
      programFeeResult: null,
    };
  });
  return res;
}

/**
 * Merge new programFees data into application store
 * @param programFeesStore programFees data in application store
 * @param programFees programFees data needs to be merged
 * @param taxStatus RequestStatus to be used to set for taxes
 * @returns merged ProgramFeesStore
 */
function mergeProgramFeesStore(
  programFeesStore: ProgramFeesStore | null,
  programFees: ProgramFees,
  taxStatus: RequestStatus,
): ProgramFeesStore {
  const addingProgramFeeResultWithStatusMap =
    convertToProgramFeeResultWithStatusMap(
      programFees.programFeeResultMap,
      RequestStatus.SUCCESS,
      taxStatus,
    );
  let programFeeResultWithStatusMap;
  if (programFeesStore == null) {
    programFeeResultWithStatusMap = addingProgramFeeResultWithStatusMap;
  } else {
    programFeeResultWithStatusMap = mergeProgramFeeResultWithStatusMap(
      programFeesStore.programFeeResultWithStatusMap,
      addingProgramFeeResultWithStatusMap,
    );
  }
  return {
    countryCode: programFees.countryCode,
    asin: programFees.asin,
    fnsku: programFees.fnsku,
    merchantId: programFees.merchantId,
    inventoryUnitInfo: programFees.inventoryUnitInfo,
    programFeeResultWithStatusMap,
  };
}

function convertToProgramFeeResultWithStatusMap(
  programFeeResultMap: Record<string, ProgramFeeResult>,
  requestStatus: RequestStatus,
  taxStatus: RequestStatus,
): Record<string, ProgramFeeResultWithStatus> {
  const res: Record<string, ProgramFeeResultWithStatus> = {};
  for (const key in programFeeResultMap) {
    res[key] = {
      taxStatus,
      requestStatus,
      programFeeResult: programFeeResultMap[key],
    };
  }
  return res;
}

function mergeProgramFeeResultWithStatusMap(
  programFeeResultWithStatusMap: Record<string, ProgramFeeResultWithStatus>,
  addingProgramFeeResultWithStatusMap: Record<
    string,
    ProgramFeeResultWithStatus
  >,
): Record<string, ProgramFeeResultWithStatus> {
  const res: Record<string, ProgramFeeResultWithStatus> = {};
  if (programFeeResultWithStatusMap != null) {
    for (const key in programFeeResultWithStatusMap) {
      res[key] = programFeeResultWithStatusMap[key];
    }
  }
  if (programFeeResultWithStatusMap != null) {
    for (const key in addingProgramFeeResultWithStatusMap) {
      res[key] = addingProgramFeeResultWithStatusMap[key];
    }
  }
  return res;
}
